import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Calculate,
  LocalHospital,
  Info,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Warning,
  TrendingUp,
  CalendarMonth
} from '@mui/icons-material';
import api from './axiosConfig';

const AWE_METHOD_LABELS = {
  full_period: 'Calculated from 8 weeks of payroll history',
  partial_period: 'Calculated from partial payroll history (fewer than 8 weeks on record)',
  contractual_estimate: 'Estimated from contracted hourly rate — no payroll history on file yet'
};

const SSPCalculator = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [sicknessStart, setSicknessStart] = useState('');
  const [sicknessEnd, setSicknessEnd] = useState('');
  const [qualifyingDays, setQualifyingDays] = useState(5);

  const [result, setResult] = useState(null);
  const [rates, setRates] = useState(null);
  const [history, setHistory] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRates, setShowRates] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchRates();
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      fetchHistory(selectedEmployee);
    }
  }, [selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.employees || response.data || []);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchRates = async () => {
    try {
      const response = await api.get('/ssp/rates');
      setRates(response.data);
    } catch (err) {
      console.error('Failed to fetch SSP rates:', err);
    }
  };

  const fetchHistory = async (employeeId) => {
    try {
      const response = await api.get(`/ssp/history/${employeeId}`);
      setHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const handleCalculate = async () => {
    if (!selectedEmployee || !sicknessStart || !sicknessEnd) {
      setError('Please select an employee and enter sickness dates');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const response = await api.post('/ssp/calculate', {
        employee_id: parseInt(selectedEmployee),
        sickness_start: sicknessStart,
        sickness_end: sicknessEnd,
        qualifying_days_per_week: qualifyingDays
      });

      setResult(response.data);
    } catch (err) {
      // SSPCalculationError surfaces here as a 400 with { error, refused: true } —
      // show it directly rather than a generic failure message, since it tells
      // the admin exactly what's missing (e.g. no payroll history + no hourly rate).
      setError(err.response?.data?.error || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <LocalHospital sx={{ fontSize: 40, color: '#e53935' }} />
          SSP Calculator
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Calculate Statutory Sick Pay entitlement for employees
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Input Form */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Calculate SSP
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
              <TextField
                select
                label="Employee"
                value={selectedEmployee}
                onChange={(e) => {
                  setSelectedEmployee(e.target.value);
                  setResult(null);
                }}
                fullWidth
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.name || `${emp.first_name} ${emp.last_name}`}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                type="date"
                label="Sickness Start Date"
                value={sicknessStart}
                onChange={(e) => setSicknessStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <TextField
                type="date"
                label="Sickness End Date"
                value={sicknessEnd}
                onChange={(e) => setSicknessEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <TextField
                select
                label="Qualifying Days per Week"
                value={qualifyingDays}
                onChange={(e) => setQualifyingDays(parseInt(e.target.value))}
                helperText="Days employee normally works"
                fullWidth
              >
                {[1, 2, 3, 4, 5, 6, 7].map((days) => (
                  <MenuItem key={days} value={days}>{days} days</MenuItem>
                ))}
              </TextField>

              {error && <Alert severity="error">{error}</Alert>}

              <Button
                variant="contained"
                size="large"
                onClick={handleCalculate}
                disabled={loading || !selectedEmployee}
                startIcon={loading ? <CircularProgress size={20} /> : <Calculate />}
                sx={{ mt: 1 }}
              >
                Calculate SSP
              </Button>
            </Box>
          </Paper>

          {/* Current Rates Card */}
          <Paper sx={{ p: 2, mt: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ cursor: 'pointer' }}
              onClick={() => setShowRates(!showRates)}
            >
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info color="primary" />
                Current SSP Rates & Rules
              </Typography>
              <IconButton size="small">
                {showRates ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            <Collapse in={showRates}>
              {rates && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Weekly Rate</Typography>
                      <Typography variant="h6">{formatCurrency(rates.weekly_rate)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Lower Earnings Limit</Typography>
                      <Typography variant="h6">
                        {rates.lel_abolished ?
                          <Chip label="Abolished" size="small" color="success" /> :
                          '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Waiting Days</Typography>
                      <Typography variant="h6">
                        {rates.waiting_days_abolished ?
                          <Chip label="Abolished" size="small" color="success" /> :
                          '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Maximum Duration</Typography>
                      <Typography variant="h6">{rates.max_weeks} weeks</Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Alert severity="info" icon={<TrendingUp />}>
                    <Typography variant="body2" fontWeight="500">
                      {rates.calculation_method}
                    </Typography>
                    <Typography variant="caption">
                      Effective from {rates.effective_from ? new Date(rates.effective_from).toLocaleDateString('en-GB') : '—'}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Collapse>
          </Paper>
        </Grid>

        {/* Results Panel */}
        <Grid item xs={12} md={6}>
          {result && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {result.entitlement_exhausted ? <Warning color="warning" /> : <CheckCircle color="success" />}
                Calculation Result
              </Typography>

              {/* AWE method banner — replaces the old pre-flight eligibility check.
                  Under the 2026/27 rules everyone's eligible; this tells the admin
                  where the earnings figure came from instead. */}
              {result.awe_method && (
                <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="caption">
                    {AWE_METHOD_LABELS[result.awe_method] || result.awe_method}
                  </Typography>
                </Alert>
              )}

              {result.entitlement_exhausted && (
                <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="body2" fontWeight="500">
                    28-week entitlement exhausted for this linked sickness period
                  </Typography>
                  <Typography variant="caption">
                    {result.days_already_paid} days already paid within the linked period of incapacity for work
                  </Typography>
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                {/* Main Result */}
                <Card sx={{ bgcolor: result.entitlement_exhausted ? 'warning.light' : 'success.light', mb: 3 }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color={result.entitlement_exhausted ? 'warning.dark' : 'success.dark'}>
                      Total SSP Payable
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color={result.entitlement_exhausted ? 'warning.dark' : 'success.dark'}>
                      {formatCurrency(result.ssp_amount)}
                    </Typography>
                    <Typography variant="body2" color={result.entitlement_exhausted ? 'warning.dark' : 'success.dark'}>
                      for {result.ssp_days} qualifying days
                    </Typography>
                  </CardContent>
                </Card>

                {/* Breakdown Table */}
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Sickness Period</TableCell>
                      <TableCell align="right">
                        {new Date(result.sickness_start).toLocaleDateString('en-GB')} - {new Date(result.sickness_end).toLocaleDateString('en-GB')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Total Calendar Days</TableCell>
                      <TableCell align="right">{result.total_calendar_days} days</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        Waiting Days
                        <Chip label="Abolished" size="small" color="success" sx={{ ml: 1 }} />
                      </TableCell>
                      <TableCell align="right">0 days (unpaid)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>SSP Days Payable</TableCell>
                      <TableCell align="right"><strong>{result.ssp_days} days</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Daily Rate</TableCell>
                      <TableCell align="right">{formatCurrency(result.daily_rate)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Average Weekly Earnings</TableCell>
                      <TableCell align="right">{formatCurrency(result.awe)}/week</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Days Already Paid (linked period)</TableCell>
                      <TableCell align="right">{result.days_already_paid} days</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          )}

          {/* Sickness History */}
          {history && history.absences?.length > 0 && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ cursor: 'pointer' }}
                onClick={() => setShowHistory(!showHistory)}
              >
                <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonth color="primary" />
                  Sickness History
                </Typography>
                <IconButton size="small">
                  {showHistory ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              <Collapse in={showHistory}>
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {history.total_sick_days} sick days recorded • ~{history.estimated_ssp_weeks_used} weeks SSP used • ~{history.estimated_remaining_weeks} weeks remaining (estimate — see calculation result above for the exact linked-period figure)
                  </Alert>

                  <Table size="small">
                    <TableBody>
                      {history.absences.map((absence, idx) => (
                        <TableRow key={absence.id || idx}>
                          <TableCell>
                            {new Date(absence.start_date).toLocaleDateString('en-GB')} - {new Date(absence.end_date).toLocaleDateString('en-GB')}
                          </TableCell>
                          <TableCell align="right">{absence.days} days</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </Paper>
          )}

          {/* Empty State */}
          {!result && (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Calculate sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Select an employee and enter sickness dates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The calculator will determine SSP amount from payroll history
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SSPCalculator;